import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import BreezeCandlestickChart, { OhlcBar } from '../breeze/BreezeCandlestickChart';
import { StockBreezeSyncResult } from '../../../services/request/StockRegistry';

interface Props {
  open: boolean;
  onClose: () => void;
  data: StockBreezeSyncResult | null;
}

const StockBreezeDetailDialog: React.FC<Props> = ({ open, onClose, data }) => {
  const bars = useMemo((): OhlcBar[] => {
    const raw = data?.chart?.bars;
    if (!Array.isArray(raw)) return [];
    return raw.map((b: Record<string, unknown>) => ({
      datetime: String(b.datetime ?? ''),
      open: Number(b.open ?? 0),
      high: Number(b.high ?? 0),
      low: Number(b.low ?? 0),
      close: Number(b.close ?? 0),
      volume: Number(b.volume ?? 0),
    }));
  }, [data]);

  const analysis = (data?.chart?.analysis ?? {}) as Record<string, unknown>;
  const quote = (data?.quotes?.quote ?? data?.quotes) as Record<string, unknown> | undefined;
  const en = data?.enrichment;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Breeze market data — {data?.stock?.companyName ?? ''}{' '}
        ({data?.stock?.nseSymbol || data?.stock?.bseSymbol})
      </DialogTitle>
      <DialogContent dividers>
        {!data && <Typography color="text.secondary">No sync data</Typography>}
        {data?.status === 'FAILED' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(data.errors || []).join(' ')}
          </Alert>
        )}
        {data?.warnings && data.warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {data.warnings.map((w, i) => (
              <div key={i}>{w}</div>
            ))}
          </Alert>
        )}

        {en && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={3}>
              <Metric label="LTP" value={formatCurrency(Number(en.ltp ?? 0))} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Metric label="Open" value={en.openPrice != null ? formatCurrency(Number(en.openPrice)) : '—'} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Metric label="High" value={en.highPrice != null ? formatCurrency(Number(en.highPrice)) : '—'} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Metric label="Low" value={en.lowPrice != null ? formatCurrency(Number(en.lowPrice)) : '—'} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Metric label="52W High" value={en.week52High != null ? formatCurrency(Number(en.week52High)) : '—'} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Metric label="52W Low" value={en.week52Low != null ? formatCurrency(Number(en.week52Low)) : '—'} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Metric label="P/E" value={en.peRatio != null ? String(en.peRatio) : '—'} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Metric label="P/B" value={en.pbRatio != null ? String(en.pbRatio) : '—'} />
            </Grid>
          </Grid>
        )}

        {analysis && Object.keys(analysis).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Signals & pattern hints
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {Object.entries(analysis).map(([k, v]) => (
                <Chip
                  key={k}
                  size="small"
                  label={`${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        )}

        {bars.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Historical chart (daily)
            </Typography>
            <BreezeCandlestickChart bars={bars} height={320} />
          </Box>
        )}

        {quote && typeof quote === 'object' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Raw quote (ICICI Breeze)
            </Typography>
            <Box
              component="pre"
              sx={{
                fontSize: 11,
                maxHeight: 200,
                overflow: 'auto',
                bgcolor: 'grey.50',
                p: 1,
                borderRadius: 1,
              }}
            >
              {JSON.stringify(quote, null, 2)}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}

export default StockBreezeDetailDialog;
